// src/pages/AdminLayout.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

axios.defaults.withCredentials = true;

export default function AdminLayout({ variant = 'full' }) {
  const [status, setStatus] = useState('available'); // 'available' | 'full'
  const [saving, setSaving] = useState(false);
  const isFull = status === 'full';

  const [notifying, setNotifying] = useState(false);
  const [notified, setNotified]   = useState(false);
  const notifiedTimerRef = useRef(null);

  const [printing, setPrinting] = useState(false); // 列印狀態
  const [previewing, setPreviewing] = useState(false); // 預覽狀態
  const [printDateDlg, setPrintDateDlg] = useState({ open: false, date: '' });
  const [doorOpening, setDoorOpening] = useState(false);

  // 桌機太寬、手機太窄：分開設定最大寬
  const [viewportWidth, setViewportWidth] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth : 1024
  ));
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isMobile = viewportWidth < 768;
  const maxWidth = isMobile ? '100%' : 1480;
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [countMenuOpen, setCountMenuOpen] = useState(false);

  // 分頁樣式
  const tabStyle = ({ isActive }) => ({
    padding: '8px 14px',
    marginRight: 0,
    border: isActive ? '1px solid #5e3a1a' : '1px solid #9ca3af',
    borderRadius: 6,
    background: isActive ? '#7b4f2f' : '#f3f4f6',
    color: isActive ? '#fff' : '#1f2937',
    textDecoration: 'none',
    fontSize: 15,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  });

  const Tab = ({ to, end, children }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, marginRight: 8 }}>
      <NavLink to={to} end={end} style={tabStyle}>{children}</NavLink>
    </span>
  );

  // 分頁定義（順序即顯示順序）。count 版本只保留 forCount 的分頁。
  const TAB_DEFS = [
    { to: '', end: true, label: '會員', forCount: true },
    { to: 'list', label: '列表', forCount: true },
    { to: 'coupons', label: '優惠券' },
    { to: 'bookings', label: '預約', forCount: true },
    { to: 'seat-card-layout', label: '預約牌擺放', forCount: true },
    { to: 'quick-print', label: '上傳列印', forCount: true },
    { to: 'member-panel', label: '櫃台', forCount: true },
    { to: 'operate-logs', label: '操作明細', forCount: true },
    { to: 'banquet', label: '包場', forCount: true },
    { to: 'locker-board', label: '置物櫃', forCount: true },
    { to: 'inventory', label: '點貨' },
    { to: 'weekly-activity', label: '週活動' },
    { to: 'shift', label: '班別管理' },
    { to: 'message-campaign', label: '訊息發送' },
    { to: 'track', label: '任務追蹤' },
    { to: 'feedback', label: '顧客回饋', forCount: true },
    { to: 'member-benefits', label: '會員權益' },
    { to: 'floor-open', label: '樓層開放' },
    { to: 'cms', label: 'CMS頁面' },
    { to: 'check-panel', label: '進出場管理' },
  ];
  const isCount = variant === 'count';
  // /count 分頁順序：會員、列表、預約、預約牌、櫃台、置物櫃、包場
  const COUNT_ORDER = ['', 'list', 'bookings', 'seat-card-layout', 'quick-print', 'member-panel', 'operate-logs', 'locker-board', 'banquet', 'feedback'];
  const tabs = isCount
    ? COUNT_ORDER.map(to => TAB_DEFS.find(t => t.to === to)).filter(Boolean)
    : TAB_DEFS;

  const basePath = isCount ? '/count' : '/admin';
  const currentTo = useMemo(() => {
    const rest = location.pathname.replace(new RegExp(`^${basePath}/?`), '').replace(/\/$/, '');
    // 巢狀路由（如 track/boards/:id）對齊父分頁
    if (rest === 'track' || rest.startsWith('track/')) return 'track';
    return rest;
  }, [location.pathname, basePath]);
  const currentLabel = (tabs.find((t) => t.to === currentTo) || tabs[0])?.label || '後台';

  const dockItems = isCount
    ? [
      { to: '', label: '會員' },
      { to: 'bookings', label: '預約' },
      { to: 'member-panel', label: '櫃台' },
    ]
    : [
      { to: 'bookings', label: '預約' },
      { to: 'banquet', label: '包場' },
      { to: 'weekly-activity', label: '週活動' },
    ];
  const dockToSet = new Set(dockItems.map((d) => d.to));
  const moreIsActive = !dockToSet.has(currentTo);

  const moreGroups = isCount
    ? [
      { title: '現場', keys: ['', 'list', 'seat-card-layout', 'member-panel', 'operate-logs', 'locker-board'] },
      { title: '其他', keys: ['banquet', 'feedback'] },
    ]
    : [
      { title: '現場', keys: ['', 'list', 'seat-card-layout', 'member-panel', 'operate-logs', 'locker-board', 'check-panel', 'floor-open'] },
      { title: '活動', keys: ['weekly-activity', 'banquet', 'cms'] },
      { title: '營運', keys: ['inventory', 'shift', 'coupons', 'message-campaign', 'track', 'feedback', 'member-benefits'] },
    ];

  const hrefFor = (to) => (to ? `${basePath}/${to}` : basePath);

  // 讀取今日座位狀態
  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/seat/status');
      setStatus(data?.status === 'full' ? 'full' : 'available');
    } catch {
      setStatus('available');
    }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  /** 遠端強制開門（後端呼叫 Miezo /room?QRID&action=forceopen&KEY） */
  const forceOpenDoor = async () => {
    if (doorOpening) return;
    setDoorOpening(true);
    try {
      const { data } = await axios.post('/api/admin/room-door/force-open');
      if (data?.ok) {
        alert(data?.detail ? `開門指令已送出。\n回應：${data.detail}` : '開門指令已送出。');
      } else {
        alert('開門失敗，請稍後再試。');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e?.message || '未知錯誤';
      alert(`開門失敗：${msg}`);
    } finally {
      setDoorOpening(false);
    }
  };

  // 切換客滿/有空位
  const toggle = async () => {
    if (saving) return;
    const next = isFull ? 'available' : 'full';
    setSaving(true);
    try {
      await axios.post('/api/seat/status', { status: next });
      setStatus(next);
    } finally {
      setSaving(false);
    }
  };

  // 通知包場/外場 → LINE 群組
  const notifyBanquet = async () => {
    if (notifying) return;
    setNotifying(true);
    try {
      await axios.post('/api/banquet/notify-staff', {
        summary: status ? `座位狀態：${status}` : undefined,
      });
      setNotified(true);
      clearTimeout(notifiedTimerRef.current);
      notifiedTimerRef.current = setTimeout(() => setNotified(false), 15000);
    } catch (e) {
      console.warn('[notifyBanquet]', e?.message || e);
      alert(e?.response?.data?.error || e?.message || '通知發送失敗');
    } finally {
      setNotifying(false);
    }
  };
  useEffect(() => () => { clearTimeout(notifiedTimerRef.current); }, []);

  const btnBusy = saving || notifying || printing || doorOpening;
  const btnStyleBase = {
    borderRadius: 10,
    border: '2px solid #ccc',
    padding: '10px 16px',
    fontWeight: 900,
    fontSize: 16,
    lineHeight: 1.2,
    cursor: btnBusy ? 'not-allowed' : 'pointer',
    opacity: btnBusy ? 0.75 : 1,
    userSelect: 'none',
    transition: 'transform .08s ease, box-shadow .15s ease, background .15s ease, color .15s ease, border-color .15s ease',
    minWidth: 132,
    minHeight: 44,
    textAlign: 'center',
  };

  const todayYmd = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };

  const openPrintDateDlg = () => {
    setPrintDateDlg({ open: true, date: todayYmd() });
  };

  // 預覽 PDF（生成 PDF 並在新視窗顯示）
  const handlePreview = async (selectedDate) => {
    if (previewing) return;
    setPreviewing(true);
    try {
      const date = selectedDate || todayYmd();
      const { data } = await axios.post('/api/admin/seat-bookings/preview', { date });
      if (data?.pdf) {
        // 將 base64 轉換為 Blob
        const binaryString = atob(data.pdf);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
        
        // 創建 URL 並在新視窗打開
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const previewWindow = window.open(pdfUrl, '_blank');
        if (previewWindow) {
          previewWindow.focus();
        } else {
          alert('無法打開預覽視窗，請檢查瀏覽器彈出視窗設定');
        }
        // 清理 URL（延遲清理，確保視窗已載入）
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
      } else {
        alert('預覽生成失敗：無法取得 PDF 資料');
      }
    } catch (e) {
      console.error('preview error:', e);
      const errorMsg = e?.response?.data?.message || e?.message || '未知錯誤';
      alert(`預覽失敗：${errorMsg}`);
    } finally {
      setPreviewing(false);
    }
  };

  // 伺服器本機直接列印（PDFKit + Sumatra/Adobe）
  const handleServerPrintDirect = async (selectedDate) => {
    if (printing) return;
    setPrinting(true);
    setPrintDateDlg(p => ({ ...p, open: false }));
    try {
      const date = selectedDate || todayYmd();
      const { data } = await axios.post('/api/admin/seat-bookings/print-direct', { date });
      if (data?.printed) {
        alert(`已送印：${data.sent} 張（${data.date}）`);
      } else {
        alert(`PDF 已生成但未自動列印：\n${data?.file || ''}\n${data?.hint || ''}`);
      }
    } catch (e) {
      console.error('server print error:', e);
      const errorMsg = e?.response?.data?.message || e?.message || '未知錯誤';
      alert(`列印失敗：${errorMsg}\n\n請檢查伺服器環境/印表機設定`);
    } finally {
      setPrinting(false);
    }
  };

  const menuItemBtn = {
    display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px',
    border: 'none', background: 'transparent', fontSize: 15, fontWeight: 700, cursor: 'pointer',
  };

  return (
    <main
      style={{
        maxWidth,
        width: '100%',
        margin: '0 auto',
        padding: isMobile ? '8px 12px calc(72px + env(safe-area-inset-bottom, 0px))' : '14px 20px 20px',
        height: '100dvh',
        boxSizing: 'border-box',
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        rowGap: isMobile ? 8 : 12,
        overflowX: 'hidden',
        overflowY: 'hidden',
      }}
    >
      {isMobile ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: 44 }}>
          <div style={{ fontWeight: 800, fontSize: 17, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {currentLabel}
          </div>
          {isCount && (
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => setCountMenuOpen((v) => !v)}
                style={{ padding: '6px 12px', fontWeight: 800, fontSize: 16, border: '1px solid #d1d5db', borderRadius: 8, background: '#fff' }}
              >
                ⋯
              </button>
              {countMenuOpen && (
                <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 40, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.12)', minWidth: 180, padding: 8 }}>
                  <button type="button" style={menuItemBtn} onClick={() => { toggle(); setCountMenuOpen(false); }}>{saving ? '處理中…' : (isFull ? '客滿' : '有空位')}</button>
                  <button type="button" style={menuItemBtn} onClick={() => { forceOpenDoor(); setCountMenuOpen(false); }}>{doorOpening ? '開門中…' : '開門'}</button>
                  <button type="button" style={menuItemBtn} onClick={() => { notifyBanquet(); setCountMenuOpen(false); }}>{notifying ? '通知中…' : (notified ? '已通知' : '通知包場')}</button>
                  <button type="button" style={menuItemBtn} onClick={() => { openPrintDateDlg(); setCountMenuOpen(false); }}>{printing ? '送印中…' : '列印預約牌'}</button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, minWidth: 0 }}>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'nowrap', overflowX: 'auto', WebkitOverflowScrolling: 'touch', minWidth: 0, paddingBottom: 4 }}>
          {tabs.map(t => (
            <Tab key={t.to || 'root'} to={t.to} end={t.end}>{t.label}</Tab>
          ))}
        </nav>

        {isCount && (
        <div style={{ display: 'flex', gap: 10 }}>
          {/* 保留：客滿/有空位 */}
          <button
            onClick={toggle}
            disabled={saving}
            title={isFull ? '目前顯示「客滿」；點擊切回「有空位」' : '目前顯示「有空位」；點擊切換成「客滿」'}
            style={{
              ...btnStyleBase,
              background: isFull ? '#e53935' : '#374151',
              color: '#fff',
              borderColor: isFull ? '#e53935' : '#374151',
              boxShadow: isFull
                ? '0 6px 14px rgba(229,57,53,.28)'
                : '0 2px 8px rgba(55,65,81,.25)',
            }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
            onMouseUp={(e)   => { e.currentTarget.style.transform = 'scale(1)'; }}
            onMouseLeave={(e)=> { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {saving ? '處理中…' : (isFull ? '客滿' : '有空位')}
          </button>

          <button
            type="button"
            onClick={forceOpenDoor}
            disabled={doorOpening}
            title="遠端強制開門（Miezo：/room?action=forceopen）"
            style={{
              ...btnStyleBase,
              background: '#15803d',
              color: '#fff',
              borderColor: '#166534',
              boxShadow: '0 4px 12px rgba(22,101,52,.25)',
            }}
            onMouseDown={(e) => { if (!doorOpening) e.currentTarget.style.transform = 'scale(0.98)'; }}
            onMouseUp={(e)   => { e.currentTarget.style.transform = 'scale(1)'; }}
            onMouseLeave={(e)=> { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {doorOpening ? '開門中…' : '開門'}
          </button>

          {/* 保留：通知包場/外場 */}
          <button
            onClick={notifyBanquet}
            disabled={notifying}
            title="通知包場/外場人員處理（n8n webhook）"
            style={{
              ...btnStyleBase,
              background: notified ? '#2563eb' : '#6b7280',
              color: '#fff',
              borderColor: notified ? '#2563eb' : '#6b7280',
              boxShadow: notified
                ? '0 6px 14px rgba(37,99,235,.28)'
                : '0 2px 8px rgba(107,114,128,.25)',
            }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
            onMouseUp={(e)   => { e.currentTarget.style.transform = 'scale(1)'; }}
            onMouseLeave={(e)=> { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {notifying ? '通知中…' : (notified ? '已通知' : '通知包場人員')}
          </button>

          {/* 預覽 PDF - 暫時註釋 */}
          {/* <button
            onClick={handlePreview}
            disabled={previewing}
            title="預覽列印內容（A4/每頁 8 張；無外框；含備註）"
            style={{
              ...btnStyleBase,
              background: '#fff',
              color: '#000',
              borderColor: '#ccc',
              boxShadow: '0 2px 8px rgba(0,0,0,.08)',
            }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
            onMouseUp={(e)   => { e.currentTarget.style.transform = 'scale(1)'; }}
            onMouseLeave={(e)=> { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {previewing ? '生成中…' : '預覽列印'}
          </button> */}

          {/* 直接列印（後端 PDFKit + Sumatra/Adobe） */}
          <button
            onClick={openPrintDateDlg}
            disabled={printing}
            title="伺服器本機直接列印（A4/每頁 8 張；無外框；含備註）"
            style={{
              ...btnStyleBase,
              background: '#7b4f2f',
              color: '#fff',
              borderColor: '#5e3a1a',
              boxShadow: '0 2px 8px rgba(94,58,26,.25)',
            }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
            onMouseUp={(e)   => { e.currentTarget.style.transform = 'scale(1)'; }}
            onMouseLeave={(e)=> { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {printing ? '送印中…' : '列印座位預約牌'}
          </button>

        </div>
        )}
      </div>
      )}
      {printDateDlg.open && (
        <div
          onClick={() => setPrintDateDlg(p => ({ ...p, open: false }))}
          style={{
            position: 'fixed', inset: 0, zIndex: 9000,
            background: 'rgba(0,0,0,.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 16, padding: isMobile ? '20px 16px' : '28px 32px',
              boxShadow: '0 8px 32px rgba(0,0,0,.18)',
              width: 'min(400px, calc(100vw - 32px))',
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 18 }}>選擇列印日期</div>
            <input
              type="date"
              value={printDateDlg.date}
              onChange={e => setPrintDateDlg(p => ({ ...p, date: e.target.value }))}
              style={{
                width: '100%', padding: '10px 14px', fontSize: 16,
                border: '1.5px solid #ccc', borderRadius: 10,
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setPrintDateDlg(p => ({ ...p, open: false }))}
                style={{
                  padding: '10px 20px', fontSize: 15, fontWeight: 700,
                  borderRadius: 10, border: '1.5px solid #ccc',
                  background: '#f5f5f5', color: '#333', cursor: 'pointer',
                }}
              >
                取消
              </button>
              <button
                onClick={() => handleServerPrintDirect(printDateDlg.date)}
                disabled={!printDateDlg.date}
                style={{
                  padding: '10px 20px', fontSize: 15, fontWeight: 700,
                  borderRadius: 10, border: 'none',
                  background: printDateDlg.date ? '#007bff' : '#ccc',
                  color: '#fff', cursor: printDateDlg.date ? 'pointer' : 'not-allowed',
                }}
              >
                確認列印
              </button>

            </div>
          </div>
        </div>
      )}

      {/* 下方：內容區（可上下滾動，自適應高度） */}
      <div data-admin-content="" style={{ minHeight: 0, minWidth: 0, height: '100%', overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Outlet />
      </div>

      {isMobile && (
        <>
          <nav
            style={{
              position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 80,
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
              background: '#fff', borderTop: '1px solid #e5e7eb',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              height: 56,
            }}
          >
            {dockItems.map((d) => {
              const on = currentTo === d.to;
              return (
                <button
                  key={d.to || 'home'}
                  type="button"
                  onClick={() => { setMoreOpen(false); navigate(hrefFor(d.to)); }}
                  style={{
                    border: 'none', background: 'transparent', fontWeight: 800, fontSize: 13,
                    color: on ? '#7b4f2f' : '#6b7280',
                  }}
                >
                  {d.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              style={{
                border: 'none', background: 'transparent', fontWeight: 800, fontSize: 13,
                color: moreIsActive || moreOpen ? '#7b4f2f' : '#6b7280',
              }}
            >
              更多
            </button>
          </nav>
          {moreOpen && (
            <div
              onClick={() => setMoreOpen(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 81, background: 'rgba(0,0,0,.35)' }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute', left: 0, right: 0, bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
                  maxHeight: '55vh', overflowY: 'auto',
                  background: '#fff', borderRadius: '16px 16px 0 0', padding: '12px 16px 20px',
                }}
              >
                {moreGroups.map((g) => (
                  <div key={g.title} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#9ca3af', margin: '8px 0' }}>{g.title}</div>
                    {g.keys.map((k) => {
                      const t = TAB_DEFS.find((x) => x.to === k);
                      if (!t) return null;
                      if (isCount && !tabs.some((x) => x.to === k)) return null;
                      const on = currentTo === k;
                      return (
                        <button
                          key={k || 'root'}
                          type="button"
                          onClick={() => { navigate(hrefFor(k)); setMoreOpen(false); }}
                          style={{
                            display: 'block', width: '100%', textAlign: 'left',
                            padding: '12px 8px', border: 'none', borderBottom: '1px solid #f3f4f6',
                            background: on ? '#f7efe8' : 'transparent',
                            fontWeight: 700, fontSize: 16, color: '#111',
                          }}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
}
